import { bindActionCreators } from "@reduxjs/toolkit";
import { connect } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import productActions from "../../../../store/actions/items/item.actions";
import { SkeletonSelect } from "../../../general/skeleton";
import useDelayedFlag from "../../../../hooks/useDelayedFlag";
import AlertService from "../../../../services/alerts/alert.service";
import TableElementComponent from "../../../general/elements/TableElementComponent";
import SelectInputElementComponent from "../../../general/elements/inputElements/SelectInputElementComponent";
import validationHelper from "../../../../helpers/validation.helper";

export const UpdateRelatedItemsDialogComponent = ({ productActions }) => {
  const [searchParams] = useSearchParams();
  const [itemListOptions, setItemListOptions] = useState([]);
  const [selectedRelatedItemId, setSelectedRelatedItemId] = useState(null);
  const [item, setItem] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);

  const itemId = searchParams.get("itemId");
  const showSkeleton = useDelayedFlag(!item || loading);

  useEffect(() => {
    fetchAllItems();
  }, []);

  useEffect(() => {
    if (itemId) {
      fetchItemData();
    }
  }, [itemId]);

  // Fetch all items (for selection dropdown)
  const fetchAllItems = async () => {
    try {
      const params = { getAll: true };
      const response = await productActions.getItems(params);
      if (validationHelper.validateHttpResponse(response)) {
        setItemListOptions(response.payload.items);
      }
    } catch (error) {
      console.error("Error fetching item list:", error);
    }
  };

  // Fetch current item details
  const fetchItemData = async () => {
    setLoading(true);
    try {
      const response = await productActions.getProductById(itemId);
      if (response) {
        setItem(response);
      }
    } catch (error) {
      console.error("Error fetching item:", error);
    }
    setLoading(false);
  };

  // Handle selection change
  const handleSelectChange = (e) => {
    setSelectedRelatedItemId(e.target.value);
  };

  // Add selected related item
  const handleAddNewRelatedItem = () => {
    if (!selectedRelatedItemId) {
      setErrors({ relatedItems: "יש לבחור פריט להוספה." });
      return;
    }

    if (item.relatedItems.some((relatedItem) => relatedItem.relatedItemId === selectedRelatedItemId)) {
      setErrors({ relatedItems: "פריט זה כבר מקושר." });
      return;
    }

    const selectedItem = itemListOptions.find((opt) => opt.id === selectedRelatedItemId);
    if (!selectedItem) return;

    const newRelatedItem = {
      relatedItemId: selectedItem.id,
      related: {
        itemId: selectedItem.itemId,
        itemDescription: selectedItem.itemDescription,
      },
    };

    setItem({ ...item, relatedItems: [...item.relatedItems, newRelatedItem] });
    setSelectedRelatedItemId(null);
    setErrors({});
  };

  // Remove related item
  const handleDeleteRelatedItem = (relatedItemId) => {
    const updatedRelatedItems = item.relatedItems.filter((relatedItem) => relatedItem.relatedItemId !== relatedItemId);
    setItem({ ...item, relatedItems: updatedRelatedItems });
  };

  // Submit changes
  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      const response = await productActions.updateItemRelatedItemsExtension(
        item.itemId,
        item.relatedItems.map((rel) => rel.relatedItemId)
      );
      if (!validationHelper.validateHttpResponse(response)) {
        setErrors({ general: "אירעה שגיאה בעדכון הפריטים המקושרים. נסה שוב מאוחר יותר." });
        return;
      }
      AlertService.success(`הפריטים המקושרים לפריט ${item.itemId} - ${item.itemDescription} עודכנו בהצלחה`);
    } catch (error) {
      setErrors({ general: "אירעה שגיאה בעדכון הפריטים המקושרים. נסה שוב מאוחר יותר." });
    }
  };

  // Dropdown for selecting related items
  const getItemListOptionsSelectElement = () => {
    const itemsOptions = itemListOptions
      ?.filter((opt) => opt.id !== item?.id) // Exclude the current item
      .map((opt) => ({ value: opt.id, label: `${opt.itemId} - ${opt.itemDescription}` }));

    return (
      <SelectInputElementComponent
        props={{
          name: "relatedItems",
          value: selectedRelatedItemId,
          options: itemsOptions,
          onChange: handleSelectChange,
          searchable: true,
        }}
      />
    );
  };

  // Table for displaying related items
  const getRelatedItemsTableElement = () => {
    // const columns = [{ label: "שם פריט", field: "related.itemDescription", render: (value, row) => <span>{row.related?.itemDescription}</span> }];

    const columns = [
      {
        label: "שם פריט",
        field: "related.itemDescription",
        render: (value, row) => {
          return <span>{row.related?.itemDescription}</span>;
        },
      },
    ];

    const actions = [
      {
        label: "מחיקה",
        icon: "fa-solid fa-trash",
        className: "greenButton",
        onClick: (relatedItem) => handleDeleteRelatedItem(relatedItem.relatedItemId),
      },
    ];
    return <TableElementComponent columns={columns} data={item.relatedItems} actions={actions} />;
  };

  if (!item || loading) {
    return <div className="dialogContainer flex fdColumn gap10">{showSkeleton && <SkeletonSelect />}</div>;
  }

  return (
    <div className="dialogContainer flex fdColumn gap10">
      <h1>
        פריטים מקושרים - {item.itemId} {item.itemDescription}
      </h1>

      {/* Select Related Item */}
      <div className="flex fdColumn gap10">
        {getItemListOptionsSelectElement()}
        <button className="primaryButton" onClick={handleAddNewRelatedItem}>
          הוסף
        </button>
      </div>
      {errors.relatedItems && <span className="error">{errors.relatedItems}</span>}

      {/* Related Items Table */}
      <div className="attributesTableContainer">{getRelatedItemsTableElement()}</div>

      {/* Submit Form */}
      <form onSubmit={handleSubmit} className="flex fdColumn gap10">
        <button type="submit" className="primaryButton">
          עדכן
        </button>
        {errors.general && <span className="error">{errors.general}</span>}
      </form>
    </div>
  );
};

const mapStateToProps = (state) => ({
  categories: state.categoryReducer.categories?.items, // Subscribe to categories state from the reducer
});

const mapDispatchToProps = (dispatch) => ({
  productActions: bindActionCreators(productActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(UpdateRelatedItemsDialogComponent);
